import { message } from 'antd';
import { isAxiosError } from 'axios';
import { debounce, put, type Effect, type ForkEffect, takeEvery, select } from 'redux-saga/effects';
import * as Effects from 'redux-saga/effects';
import { replace } from 'redux-first-history';
import type { PayloadAction } from '@reduxjs/toolkit';

import * as Api from '~/api';
import { getWithParamString } from '~/helpers';

import { Columns } from '~/components/Campaigns/Columns';

import type { Campaign, Group } from '~/types/models';
import {
  type ErrorResponse,
  type IndexResponse,
  isErrorResponse,
  type SendUnsentEmailsResponse,
  type SendUnsentSmsResponse,
} from '~/types/responses';
import type { IRestIndexParams } from '~/types/states';

import { actions as app } from '~/redux/app/slice';
import { actions as groupActions } from '~/redux/groups/slice';
import { watchHandleError } from '~/redux/app/saga';
import { emailsSelectors, smsSelectors } from '~/redux/rootSaga';

import { actions } from './slice';
import { getItem, getParams } from './selectors';

const call: any = Effects.call;

export function* watchAddUsersFromGroup({
  payload,
}: PayloadAction<Group>): Generator<Effect, void, any> {
  yield put(actions.setLoading(true));
  yield put(groupActions.setLoading(true));
  yield put(groupActions.setError(null));

  try {
    const group: Group = yield call(Api.retrieve, `/groups/${payload.id}`, {
      with: 'users',
    });

    const item = yield select(getItem);
    yield put(actions.setItem({
      ...item,
      callers: [...item.callers, ...(group.users ?? [])].filter((v, idx, arr) => {
        return idx === arr.findIndex((i) => i.id === v.id);
      }),
    }));

    message.warning('Révisez les ajouts puis sauvegardez la campagne, le cas échéant.');
  } catch (e) {
    yield call(Api.handleError, e);

    if (isAxiosError<ErrorResponse>(e) && isErrorResponse(e.response?.data) && e.response?.data) {
      yield call(groupActions.setError, e.response.data);
      yield call(watchHandleError, e.response.data, Columns);
    }
  } finally {
    yield put(actions.setLoading(false));
    yield put(groupActions.setLoading(false));
  }
}

export function* watchCreate({ payload: item }: PayloadAction<Partial<Campaign>>): Generator<Effect, void, Campaign> {
  yield put(actions.setLoading(true));
  yield put(actions.setError(null));

  try {
    yield put(app.title('Création...'));

    const newItem = yield call(Api.create, '/campaigns', item);
    message.success('Campagne créée avec succès.');

    yield call(Api.clearCache, /.*campaigns.*/);

    yield put(replace(`/campaigns/${newItem.humanId}/edit`));
  } catch (e) {
    yield put(app.title('Nouvelle campagne'));
    yield put(actions.setLoading(false));

    yield call(Api.handleError, e);

    if (isAxiosError<ErrorResponse>(e) && isErrorResponse(e.response?.data) && e.response?.data) {
      yield call(actions.setError, e.response.data);
      yield call(watchHandleError, e.response.data, Columns);
    }
  }
}

export function* watchDestroy({ payload: { id } }: PayloadAction<Campaign>): Generator<Effect, void, void> {
  yield put(actions.setLoading(true));
  yield put(actions.setError(null));

  try {
    yield call(Api.destroy, '/campaigns', id);
    message.success('Campagne supprimée avec succès.');

    yield call(watchIndex, {});
  } catch (e) {
    yield call(Api.handleError, e);

    if (isAxiosError<ErrorResponse>(e) && isErrorResponse(e.response?.data) && e.response?.data) {
      yield call(actions.setError, e.response.data);
      yield call(watchHandleError, e.response.data, Columns);
    }
  } finally {
    yield put(actions.setLoading(false));
  }
}

export function* watchIndex({
  payload: params,
}: PayloadAction<IRestIndexParams<Campaign>>): Generator<Effect, void, IndexResponse<Campaign>> {
  yield put(actions.setLoading(true));
  yield put(actions.setError(null));

  try {
    const currentParams = yield select(getParams);
    const { currentPage, items, pageSize, total, totalPages } = yield call(Api.index, '/campaigns', {
      ...currentParams,
      ...params,
    });
    yield put(actions.setPage(currentPage));
    yield put(actions.setData(items));
    yield put(actions.setPageSize(pageSize));
    yield put(actions.setTotal(total));
    yield put(actions.setTotalPages(totalPages));
  } catch (e) {
    yield call(Api.handleError, e);

    if (isAxiosError<ErrorResponse>(e) && isErrorResponse(e.response?.data) && e.response?.data) {
      yield call(actions.setError, e.response.data);
      yield call(watchHandleError, e.response.data, Columns);
    }
  } finally {
    yield put(actions.setLoading(false));
  }
}

export function* watchRetrieve({
  payload: { id, params },
}: PayloadAction<{ id: Campaign['id'] | Campaign['humanId']; params?: IRestIndexParams<Campaign> }>): Generator<
  Effect,
  void,
  Campaign
> {
  try {
    yield put(actions.setLoading(true));
    yield put(actions.setError(null));

    let item = yield select(getItem);
    if (item?.humanId !== id) {
      yield put(app.title('Chargement...'));
      yield put(actions.setItem(null));
      yield put(actions.setInitialItem(null));

      item = yield call(Api.retrieve, `/campaigns/${id}`, {
        ...(params ?? {}),
        with: getWithParamString(Columns, 'single'),
      });
      yield put(actions.setItem(item));
      yield put(actions.setInitialItem(JSON.stringify(item)));
    }
    yield put(app.title(item.name));
  } catch (e) {
    yield call(Api.handleError, e);

    if (isAxiosError<ErrorResponse>(e) && isErrorResponse(e.response?.data) && e.response?.data) {
      yield call(actions.setError, e.response.data);
      yield call(watchHandleError, e.response.data, Columns);
    }
  } finally {
    yield put(actions.setLoading(false));
  }
}

export function* watchSendUnsentEmails({ payload: id }: PayloadAction<string>): Generator<Effect, void, any> {
  try {
    const data: SendUnsentEmailsResponse = yield call(Api.sendUnsentEmails, id);

    if (data.sent === 0) {
      message.success(`Aucun courriel ajouté: tous les courriels sont déjà en cours d'envoi.`);
    } else if (data.sent === 1) {
      message.success("Un courriel en cours d'envoi.");
    } else {
      message.success(`${data.sent} courriels en cours d'envoi.`);
    }

    const { getParams: getEmailsParams } = emailsSelectors;
    const currentParams = yield select(getEmailsParams);

    yield put({
      type: 'campaigns/emails/index',
      payload: {
        ...currentParams,
        campaign: id,
      },
    });
  } catch (e) {
    yield call(Api.handleError, e);

    if (isAxiosError<ErrorResponse>(e) && isErrorResponse(e.response?.data) && e.response?.data) {
      yield call(actions.setError, e.response.data);
      yield call(watchHandleError, e.response.data, Columns);
    }
  } finally {
    yield put(actions.setLoading(false));
  }
}

export function* watchSendUnsentSms({ payload: id }: PayloadAction<string>): Generator<Effect, void, any> {
  try {
    const data: SendUnsentSmsResponse = yield call(Api.sendUnsentSms, id);

    if (data.sent === 0) {
      message.success(`Aucun texto ajouté: tous les textos sont déjà en cours d'envoi.`);
    } else if (data.sent === 1) {
      message.success("Un texto en cours d'envoi.");
    } else {
      message.success(`${data.sent} textos en cours d'envoi.`);
    }

    const { getParams: getSmsParams } = smsSelectors;
    const currentParams = yield select(getSmsParams);

    yield put({
      type: 'campaigns/sms/index',
      payload: {
        ...currentParams,
        campaign: id,
      },
    });
  } catch (e) {
    yield call(Api.handleError, e);

    if (isAxiosError<ErrorResponse>(e) && isErrorResponse(e.response?.data) && e.response?.data) {
      yield call(actions.setError, e.response.data);
      yield call(watchHandleError, e.response.data, Columns);
    }
  } finally {
    yield put(actions.setLoading(false));
  }
}

export function* watchSetParams({
  payload: params,
}: PayloadAction<IRestIndexParams<Campaign>>): Generator<Effect, void, Campaign> {
  const { sortBy, page, pageSize, ...filters } = params;

  yield put(actions.setPage(page));
  yield put(actions.setPageSize(pageSize));
  yield put(actions.setSortBy(sortBy));
  yield put(actions.setFilters(filters));
}

export function* watchUpdate({ payload: item }: PayloadAction<Partial<Campaign>>): Generator<Effect, void, any> {
  if (!item.id) {
    return;
  }

  const initialTitle = yield select((state) => state.app.title);
  yield put(actions.setLoading(true));
  yield put(app.title('Sauvegarde...'));
  yield put(actions.setError(null));

  try {
    yield call(Api.update, `/campaigns/${item.id}`, item);
    const updatedItem = yield call(Api.retrieve, `/campaigns/${item.id}`, {
      with: getWithParamString(Columns, 'single'),
    });
    yield put(actions.setItem(updatedItem));
    yield put(actions.setInitialItem(JSON.stringify(updatedItem)));
    message.success('Campagne mise à jour avec succès.');

    yield call(Api.clearCache, /.*campaigns.*/);

    yield put(app.title(updatedItem.name));
  } catch (e) {
    yield call(Api.handleError, e);

    if (isAxiosError<ErrorResponse>(e) && isErrorResponse(e.response?.data) && e.response?.data) {
      yield call(actions.setError, e.response.data);
      yield call(watchHandleError, e.response.data, Columns);
    }
  } finally {
    yield put(app.title(item.name ?? initialTitle));
    yield put(actions.setLoading(false));
  }
}

export function* watchCampaignsSagas(): Generator<ForkEffect, void> {
  yield takeEvery(actions.addUsersFromGroup, watchAddUsersFromGroup);
  yield debounce(500, actions.index, watchIndex);
  yield debounce(100, actions.retrieve, watchRetrieve);
  yield takeEvery(actions.create, watchCreate);
  yield takeEvery(actions.destroy, watchDestroy);
  yield takeEvery(actions.setParams, watchSetParams);
  yield takeEvery(actions.update, watchUpdate);
  yield takeEvery(actions.sendUnsentEmails, watchSendUnsentEmails);
  yield takeEvery(actions.sendUnsentSms, watchSendUnsentSms);
}

const campaignsSagas = watchCampaignsSagas;

export default campaignsSagas;
